import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ViewConfig } from '@/types/views';
import { isUuid } from '@/helpers/validation';
import { useCallback, useEffect, useMemo } from 'react';
import { Loading } from '@/components/Loading';
import Header from '@/components/Header/Header';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import { CenteredColumn } from '@/components/CenteredColumn';
import { ProfileForm } from '@/components/Travellers/ProfileForm';
import { TravellerTickets } from '@/components/Travellers/TravellerTickets';
import {
  getViewsForType,
  isValidView,
  getDefaultView,
} from '@/utils/viewHelpers';
import { DataNotFound } from '@/components/DataNotFound';
import {
  useGetTravellerByID,
  useGetCollectionMembers,
  TravellerMembershipDTO,
} from '@/api/trafikportalen';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@/components/core/Button';
import { IconTicketPlus } from '@/icons';
import { ActionButtons } from '@/components/Header/ActionButtons';
import { TravellerAdvanced } from '@/components/Travellers/TravellerAdvanced';
import { TravellerStatus } from '@/components/TravellerStatus';
import { StudentMembershipInfo } from '@/components/Students/StudentMembershipInfo';
import { getTravellerStatus } from '@/helpers/status';
import {
  hasDepartmentAccess,
  hasAnyMembershipDepartmentAccess,
  hasAnyMembershipDepartmentWriteAccess,
} from '@/helpers/departmentPermissions';
import { useCanBuyTickets } from '@/hooks/useCanBuyTickets';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { DocumentHead } from '@/components/core/DocumentHead';
import {
  checkUserPermissions,
  isTenantProfile,
} from '@/components/Auth/checkUserPermissions';
import { StudentProfileForm } from '@/components/Students/StudentProfileForm';
import { StudentClaimStates } from '@/components/Students/StudentClaimStates';
import { useAuth } from '@/stores/auth';
import {
  isStudentCollectionType,
  collectionTypeToStudentType,
  studentTypeToCollectionType,
  COLLECTION_TYPE_LABELS,
  STUDENT_TYPE_VIEW,
  STUDENT_TYPE_ROUTE,
  StudentCollectionType,
} from '@/helpers/collectionTypes';
import { IconArrowRight } from '@/icons';
import type { StudentType } from '@/types/students';

export const Route = createFileRoute(
  '/$tenantId/_layout/travellers/$travellerId/$view',
)({
  component: TravellerView,
  beforeLoad: ({ params: { tenantId, travellerId, view } }) => {
    if (isTenantProfile()) {
      throw redirect({ to: '/$tenantId', params: { tenantId } });
    }
    // A hand-edited URL; the API would reject the id with 400.
    if (!isUuid(travellerId)) {
      throw redirect({ to: '/$tenantId/travellers', params: { tenantId } });
    }
    if (!isValidView(view, 'traveller')) {
      throw redirect({
        to: '/$tenantId/travellers/$travellerId/$view',
        params: {
          tenantId,
          travellerId,
          view: getDefaultView('traveller'),
        },
      });
    }
  },
});

const canSeeStudentTab = (departmentId: string) =>
  checkUserPermissions({ permission: 'organization:viewer' }) ||
  hasDepartmentAccess(departmentId);

function getStudentTabLabel(
  membership: TravellerMembershipDTO,
  allStudentMemberships: TravellerMembershipDTO[],
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string {
  const label = formatMessage(
    COLLECTION_TYPE_LABELS[membership.collection_type as StudentCollectionType],
  );
  const sameTypeMemberships = allStudentMemberships.filter(
    (m) => m.collection_type === membership.collection_type,
  );
  if (sameTypeMemberships.length > 1) {
    return `${label} - ${membership.department_name}`;
  }
  return label;
}

function TravellerView() {
  const { tenantId, travellerId, view } = Route.useParams();
  const { formatMessage } = useIntl();
  const selected = view;
  const navigate = useNavigate();

  const { data: traveller, isLoading } = useGetTravellerByID(travellerId);
  const departmentAccess = useAuth((state) => state.departmentAccess);
  const canBuyTickets = useCanBuyTickets();
  const studentsDisabled = useStudentsDisabled();

  useEffect(() => {
    if (!traveller || !departmentAccess) return;
    if (hasAnyMembershipDepartmentAccess(traveller)) return;
    navigate({
      to: '/$tenantId/travellers',
      params: { tenantId },
      replace: true,
    });
  }, [departmentAccess, navigate, tenantId, traveller]);

  useEffect(() => {
    if (!studentsDisabled || !selected.startsWith('student-')) return;

    navigate({
      to: Route.to,
      params: { tenantId, travellerId, view: 'profile' },
      replace: true,
    });
  }, [navigate, selected, studentsDisabled, tenantId, travellerId]);

  const studentMemberships = useMemo(() => {
    if (!traveller || studentsDisabled) return [];
    return traveller.memberships.filter(
      (m) =>
        isStudentCollectionType(m.collection_type) &&
        canSeeStudentTab(m.department_id),
    );
  }, [studentsDisabled, traveller]);

  const selectedStudentMembership = useMemo(() => {
    if (!selected.startsWith('student-')) return null;
    const category = selected.slice('student-'.length);
    const collectionType = studentTypeToCollectionType(category as StudentType);
    return (
      studentMemberships.find((m) => m.collection_type === collectionType) ??
      null
    );
  }, [selected, studentMemberships]);

  const { data: collectionMembers, isLoading: isLoadingCollectionMembers } =
    useGetCollectionMembers(selectedStudentMembership?.collection_id ?? '', {
      query: {
        enabled: !!selectedStudentMembership,
      },
    });

  const selectedCollectionMember = useMemo(() => {
    if (!selectedStudentMembership || !collectionMembers) return null;
    return (
      collectionMembers.find(
        (cm) => cm.collection_member.traveller_id === traveller?.traveller.id,
      ) ?? null
    );
  }, [selectedStudentMembership, collectionMembers, traveller]);

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;

      navigate({
        to: Route.to,
        params: { tenantId, travellerId, view },
        replace: false,
      });
    },
    [navigate, tenantId, travellerId],
  );

  const views = useMemo((): ViewConfig[] => {
    const staticViews = getViewsForType('traveller') as ViewConfig[];
    const profileView = staticViews
      .filter((v) => v.path === 'profile')
      .map((v) => ({ ...v, defaultMessage: 'Resenär' }));
    const remainingViews = staticViews.filter((v) => v.path !== 'profile');

    const studentViews: ViewConfig[] = studentMemberships.map((membership) => {
      const tabLabel = getStudentTabLabel(
        membership,
        studentMemberships,
        formatMessage,
      );
      const category = collectionTypeToStudentType(
        membership.collection_type as StudentCollectionType,
      );
      return {
        label: tabLabel,
        path: STUDENT_TYPE_VIEW[category],
        defaultMessage: tabLabel,
      };
    });

    return [...profileView, ...studentViews, ...remainingViews];
  }, [formatMessage, studentMemberships]);

  const metadataKeys = useMemo(() => {
    if (!traveller) {
      return {
        title: 'Resenär',
        description: 'Resenärdetaljer',
      };
    }

    const { first_name, last_name, email } = traveller.traveller;
    const travellerName =
      first_name || last_name
        ? `${first_name || ''}${last_name ? ` ${last_name}` : ''}`
        : email;

    if (selected === 'profile') {
      return {
        title: `${travellerName} - Resenär`,
        description: `Resenärinformation för ${travellerName}`,
      };
    }
    if (selected === 'tickets') {
      return {
        title: `${travellerName} - Biljetter`,
        description: `Biljetter för resenären ${travellerName}`,
      };
    }
    if (selected === 'advanced') {
      return {
        title: `${travellerName} - Avancerat`,
        description: `Avancerade inställningar för resenären ${travellerName}`,
      };
    }
    if (selectedStudentMembership) {
      const label = formatMessage(
        COLLECTION_TYPE_LABELS[
          selectedStudentMembership.collection_type as StudentCollectionType
        ],
      );
      return {
        title: `${travellerName} - ${label}`,
        description: `${label} för resenären ${travellerName}`,
      };
    }
    return {
      title: `${travellerName}`,
      description: `Detaljer för resenären ${travellerName}`,
    };
  }, [formatMessage, selected, selectedStudentMembership, traveller]);

  if (isLoading) {
    return <Loading />;
  }

  if (!traveller) {
    return (
      <DataNotFound
        description={formatMessage({ defaultMessage: 'Resenären finns inte' })}
        buttonTitle={formatMessage({ defaultMessage: 'Till resenärer' })}
        onClick={() =>
          navigate({ to: '/$tenantId/travellers', params: { tenantId } })
        }
      />
    );
  }

  const {
    traveller: { first_name, last_name, email },
  } = traveller;

  return (
    <div className="flex grow flex-col overflow-y-auto">
      <DocumentHead
        title={metadataKeys.title}
        description={metadataKeys.description}
      />
      <Header
        title={
          first_name || last_name
            ? `${first_name || ''}${last_name ? ` ${last_name}` : ''}`
            : email
        }
        description={<TravellerStatus traveller={traveller} />}
        menu={
          <HeaderMenu
            options={views}
            selected={selected}
            onViewChange={onViewChange}
          />
        }
        actions={
          canBuyTickets &&
          hasAnyMembershipDepartmentWriteAccess(traveller) && (
            <ActionButtons>
              <Button
                startIcon={<IconTicketPlus />}
                variant="filled"
                disabled={getTravellerStatus(traveller) === 'inactive'}
                onClick={() =>
                  navigate({
                    to: '/$tenantId/buy-ticket',
                    params: { tenantId },
                    search: { travellerId },
                  })
                }
              >
                <FormattedMessage defaultMessage="Köp biljett" />
              </Button>
            </ActionButtons>
          )
        }
      />

      <CenteredColumn className="grow">
        {selected === 'profile' && <ProfileForm traveller={traveller} />}
        {selected === 'tickets' && (
          <TravellerTickets travellerId={travellerId} />
        )}
        {selected === 'advanced' && <TravellerAdvanced traveller={traveller} />}
        {selectedStudentMembership && (
          <>
            <StudentMembershipInfo
              membership={selectedStudentMembership}
              collectionMember={selectedCollectionMember?.collection_member}
              travellerId={travellerId}
              action={
                canSeeStudentTab(selectedStudentMembership.department_id) ? (
                  <Button
                    to={
                      STUDENT_TYPE_ROUTE[
                        collectionTypeToStudentType(
                          selectedStudentMembership.collection_type as StudentCollectionType,
                        )
                      ]
                    }
                    params={{ tenantId }}
                    variant="ghost"
                    endIcon={<IconArrowRight />}
                  >
                    <FormattedMessage defaultMessage="Gå till kategori" />
                  </Button>
                ) : undefined
              }
            />
            {selectedCollectionMember && (
              <StudentClaimStates
                collectionId={selectedStudentMembership.collection_id}
                collectionMemberId={
                  selectedCollectionMember.collection_member.id
                }
                travellerId={travellerId}
                membership={selectedCollectionMember.collection_member}
              />
            )}
            {isLoadingCollectionMembers ? (
              <Loading />
            ) : selectedCollectionMember ? (
              <StudentProfileForm
                student={selectedCollectionMember.collection_member}
                traveller={selectedCollectionMember.traveller}
                type={collectionTypeToStudentType(
                  selectedStudentMembership.collection_type as StudentCollectionType,
                )}
                departmentId={selectedStudentMembership.department_id}
              />
            ) : null}
          </>
        )}
      </CenteredColumn>
    </div>
  );
}
